/**
 * OWNER-OPERATING-SYSTEM-V1 -- the owner Reports view model (pure).
 *
 * Reports ANALYZES; Home ANSWERS (ARCH corollary: the two never repeat
 * each other's KPI content). Every fact here arrives from an EXISTING
 * owner wire -- the funnel snapshot, the owner dashboard, THE ONE leakage
 * classifier, the marketing-spend read, the walk-in source breakdown, and
 * the attribution/bridge health reads. This module derives NOTHING new:
 * it selects, labels, and orders wire facts for the section rhythm.
 */

import type {
  CampaignLeadAttributionHealth,
  ClinicPerformanceRow,
  DashboardSummary,
  LeadWalkinBridgeHealth,
} from '@/dashboard/types';
import type { SlaLeakageResult } from '@/dashboard/sla-leakage/sla-leakage-view';
import { classShortLabel } from '@/dashboard/sla-leakage/sla-leakage-view';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';

export interface ExceptionCardView {
  key: string;
  count: string;
  title: string;
  detail: string;
  tone: 'danger' | 'pending' | 'neutral';
  to: string;
}

/** Exception-first lead cards (F6: the page LEADS with what is wrong).
 *  Quiet = healthy: a card renders ONLY for a non-zero fact. */
export function exceptionCards(
  leakage: SlaLeakageResult | null,
  summary: DashboardSummary | null,
): ExceptionCardView[] {
  const cards: ExceptionCardView[] = [];
  if (leakage !== null) {
    for (const cls of leakage.summary) {
      if (cls.count === 0) continue;
      cards.push({
        key: `leak:${cls.breachClass}`,
        count: String(cls.count),
        title: classShortLabel(cls.breachClass),
        detail: 'open -- drills to the board',
        tone: cls.severity === 'red' ? 'danger' : 'pending',
        to: '/dashboard/leakage',
      });
    }
  }
  if (summary !== null && summary.anomalyCount > 0) {
    cards.push({
      key: 'anomalies',
      count: String(summary.anomalyCount),
      title: 'Clinic flags today',
      detail: `${summary.clinicsWithIssues} clinics affected`,
      tone: 'pending',
      to: '/dashboard/leakage',
    });
  }
  return cards.sort((a, b) => (a.tone === 'danger' ? 0 : 1) - (b.tone === 'danger' ? 0 : 1));
}

export function rupeesLabel(rupees: number | null): string {
  if (rupees === null) return 'Not available';
  return formatMinorUnitsInr(Math.round(rupees * 100)).replace(/^INR/, 'Rs');
}

export function pctLabel(rate: number | null): string {
  if (rate === null) return 'Not available';
  return `${Math.round(rate * 1000) / 10}%`;
}

export interface ClinicTableRowView {
  clinicId: string;
  clinicName: string;
  recordedCollections: string;
  misRevenue: string;
  patients: string;
  walkins: string;
  noShow: string;
  billing: 'submitted' | 'pending';
  flagged: boolean;
}

/** The honest clinics table (audit row 19 executed here: ONE clinics table
 *  home, re-based on honest fields -- nullable MIS renders '-', never a
 *  confident 0; recorded collections in minor-units truth). */
export function clinicTableRows(
  clinics: readonly ClinicPerformanceRow[],
): ClinicTableRowView[] {
  return [...clinics]
    .sort((a, b) => Number(b.hasAnomalies) - Number(a.hasAnomalies))
    .map((c) => ({
      clinicId: c.clinicId,
      clinicName: c.clinicName,
      recordedCollections:
        c.recordedCollectionsMinorUnits === null ||
        c.recordedCollectionsMinorUnits === undefined
          ? '-'
          : formatMinorUnitsInr(c.recordedCollectionsMinorUnits).replace(/^INR/, 'Rs'),
      misRevenue:
        c.misRevenue === null || c.misRevenue === undefined
          ? '-'
          : rupeesLabel(c.misRevenue),
      patients: String(c.patients),
      walkins: String(c.walkins ?? 0),
      noShow: pctLabel(c.noShowRate),
      billing: c.billingStatus === 'submitted' ? 'submitted' : 'pending',
      flagged: c.hasAnomalies,
    }));
}

export interface DataQualityCellView {
  key: string;
  label: string;
  value: string;
  meta: string;
}

/**
 * A read that hit its row cap answered for PART of the period, so the cell
 * says so on its own meta line -- a truncated count rendered as a complete one
 * is the defect this exists to prevent. Strict `=== true` so a payload without
 * the field (an older wire) reads as not-capped rather than throwing.
 */
function partialSuffix(capped: boolean | undefined): string {
  return capped === true ? ' -- PARTIAL: row cap reached, narrow the period' : '';
}

/** Data-quality cells from the attribution + bridge health reads (audit
 *  rows 15/16 SIMPLIFIED here: one number + honest meta each, replacing
 *  the 14-tile sprawl; person names never render -- CONFIG-HARDCODE class
 *  closed by the fold). */
export function dataQualityCells(
  attribution: CampaignLeadAttributionHealth | null,
  bridge: LeadWalkinBridgeHealth | null,
): DataQualityCellView[] {
  const cells: DataQualityCellView[] = [];
  if (attribution !== null) {
    const t = attribution.totals;
    const pct =
      t.leadsTotal > 0
        ? `${Math.round((t.leadsWithSource / t.leadsTotal) * 1000) / 10}%`
        : 'No leads in window';
    cells.push({
      key: 'source-captured',
      label: 'Leads with a source',
      value: pct,
      meta: `${t.leadsWithSource} of ${t.leadsTotal} leads -- campaign tag on ${t.leadsWithUtmCampaign}${partialSuffix(attribution.capped)}`,
    });
  }
  if (bridge !== null) {
    const rate = bridge.totals.leadToWalkinConversionRate;
    cells.push({
      key: 'bridge-match',
      label: 'Lead to walk-in match',
      value: rate === null ? 'No matchable window' : `${Math.round(rate * 1000) / 10}%`,
      meta: `${bridge.totals.leadsMatchedToWalkin} of ${bridge.totals.leadsMatchable} matchable leads -- ${bridge.confidence.ambiguous} ambiguous${partialSuffix(bridge.capped)}`,
    });
  }
  return cells;
}

/** One disclosed count line for the exception band. */
/**
 * SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 1, DEFINITIONALLY-TODAY). Three
 * Reports bands are CORRECT to ignore the selector bar -- the leakage queue is
 * whatever is open at this moment, and the owner-dashboard read is a SINGLE
 * CLINIC DAY (getOwnerDashboard takes one `date`, defaulting to today), which
 * no 7d/30d/3m/6m preset can name. The defect was the PRESENTATION: a selector
 * bar sitting above them reads as governing them. These two lines say what the
 * band is, on the band, in the owner's own words.
 */
export const LIVE_NOW_BASIS = 'Open right now -- the selectors above do not change this';
export const TODAY_ONLY_BASIS = 'Today only -- the selectors above do not change this';

export function leakageCountLine(leakage: SlaLeakageResult): string {
  const openClasses = leakage.summary.filter((s) => s.count > 0).length;
  const items = leakage.items.length;
  return `${items} open ${items === 1 ? 'item' : 'items'} across ${openClasses} ${openClasses === 1 ? 'class' : 'classes'}`;
}
